import React, { useState, useEffect } from 'react';
import {
  getFilterConfigByRole,
  getInitialFilters,
  getActiveFilterCount,
  FILTER_TYPES
} from '../config/filterConfig';
import { apiService } from '../services/apiService';

// 折叠箭头图标
const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

// 筛选图标
const FilterIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const EMPLOYMENT_STATUS_DATE_LABELS = {
  '在職': '入職日',
  '離職': '退職日'
};

const normalizeMultiSelectValue = (value) => Array.isArray(value) ? [...new Set(value)] : [];

const areSameSelections = (left, right) => {
  const normalizedLeft = normalizeMultiSelectValue(left);
  const normalizedRight = normalizeMultiSelectValue(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every(item => normalizedRight.includes(item));
};

// 多选筛选组件
const MultiSelectFilter = ({ field, value, onChange, talentCounts }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedValues = normalizeMultiSelectValue(value);
  const activeQuickPreset = field.quickPresets?.find(preset => areSameSelections(selectedValues, preset.value));
  const summaryLabel = activeQuickPreset && activeQuickPreset.value.length > 0
    ? activeQuickPreset.label
    : selectedValues.length > 0
      ? String(selectedValues.length)
      : null;

  const handleToggle = (optionValue) => {
    const newValue = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(field.id, newValue);
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <span className="flex items-center">
          {field.label}
          {summaryLabel && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
              {summaryLabel}
            </span>
          )}
        </span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="mt-2 pl-2 space-y-2">
          {field.quickPresets?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">よく使う絞り込み:</p>
              <div className="flex flex-wrap gap-2">
                {field.quickPresets.map(preset => {
                  const isActive = areSameSelections(selectedValues, preset.value);
                  return (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => onChange(field.id, [...preset.value])}
                      className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${isActive
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {field.options.map(option => {
            const count = talentCounts?.[field.id]?.[option.value] || 0;
            return (
              <label key={option.value} className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 group-hover:text-gray-900">
                  {option.label}
                  {field.showCount && (
                    <span className="ml-1 text-xs text-gray-500">({count})</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// 单选筛选组件
const SingleSelectFilter = ({ field, value, onChange }) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {field.label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        {field.options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// 日期范围筛选组件
const DateRangeFilter = ({ field, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleQuickFilter = (quickValue) => {
    const today = new Date();
    let start = '';
    let end = '';
    let renewalPending = false;

    switch (quickValue) {
      case 'expiring_soon_30':
        start = today.toISOString().split('T')[0];
        const date30 = new Date(today);
        date30.setDate(date30.getDate() + 30);
        end = date30.toISOString().split('T')[0];
        break;
      case 'expiring_soon_60':
        start = today.toISOString().split('T')[0];
        const date60 = new Date(today);
        date60.setDate(date60.getDate() + 60);
        end = date60.toISOString().split('T')[0];
        break;
      case 'expiring_soon_90':
        start = today.toISOString().split('T')[0];
        const date90 = new Date(today);
        date90.setDate(date90.getDate() + 90);
        end = date90.toISOString().split('T')[0];
        break;
      case 'expired':
        end = today.toISOString().split('T')[0];
        break;
      case 'renewal_pending':
        renewalPending = true;
        break;
      default:
        break;
    }

    onChange(field.id, { start, end, renewalPending });
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <span className="flex items-center">
          {field.label}
          {(value.start || value.end || value.renewalPending) && (
            <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></span>
          )}
        </span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="mt-2 pl-2 space-y-3">
          {/* 快捷筛选 */}
          {field.quickFilters && field.quickFilters.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-gray-500 mb-1">クイックフィルタ:</p>
              {field.quickFilters.map(quick => (
                <button
                  key={quick.value}
                  onClick={() => handleQuickFilter(quick.value)}
                  className="w-full text-left px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  {quick.label}
                </button>
              ))}
            </div>
          )}

          {/* 自定义日期范围 */}
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">開始日:</label>
              <input
                type="date"
                value={value.start}
                onChange={(e) => onChange(field.id, { ...value, start: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">終了日:</label>
              <input
                type="date"
                value={value.end}
                onChange={(e) => onChange(field.id, { ...value, end: e.target.value })}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 数值范围筛选组件
const NumberRangeFilter = ({
  field,
  value,
  onChange,
  noChargeValue,
  unregisteredValue,
  onNoChargeToggle,
  onUnregisteredToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasNoChargeEnabled = noChargeValue === true;
  const hasUnregisteredEnabled = unregisteredValue === true;
  const isRangeDisabled = hasNoChargeEnabled || hasUnregisteredEnabled;
  const minPlaceholder = field.minInputValue !== undefined
    ? String(field.minInputValue)
    : '0';

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <span className="flex items-center">
          {field.label}
          {(value.min !== '' || value.max !== '' || hasNoChargeEnabled || hasUnregisteredEnabled) && (
            <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></span>
          )}
        </span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="mt-2 pl-2 space-y-2">
          {field.noChargeFilterId && (
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={hasNoChargeEnabled}
                onChange={(e) => onNoChargeToggle?.(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">{field.noChargeLabel || '無償'}</span>
            </label>
          )}
          {field.unregisteredFilterId && (
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={hasUnregisteredEnabled}
                onChange={(e) => onUnregisteredToggle?.(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">{field.unregisteredLabel || '未登記'}</span>
            </label>
          )}
          <div>
            <label className="block text-xs text-gray-600 mb-1">最小値:</label>
            <input
              type="number"
              min={field.minInputValue ?? undefined}
              value={value.min}
              onChange={(e) => {
                const rawValue = e.target.value;
                if (rawValue === '' || field.minInputValue === undefined) {
                  onChange(field.id, { ...value, min: rawValue });
                  return;
                }
                const numericValue = Number(rawValue);
                onChange(field.id, {
                  ...value,
                  min: Number.isFinite(numericValue) && numericValue < field.minInputValue
                    ? String(field.minInputValue)
                    : rawValue,
                });
              }}
              disabled={isRangeDisabled}
              placeholder={minPlaceholder}
              className={`w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 ${isRangeDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">最大値:</label>
            <input
              type="number"
              value={value.max}
              onChange={(e) => onChange(field.id, { ...value, max: e.target.value })}
              disabled={isRangeDisabled}
              placeholder="999999"
              className={`w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 ${isRangeDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
            />
          </div>
          {field.unit && (
            <p className="text-xs text-gray-500">単位: {field.unit}</p>
          )}
        </div>
      )}
    </div>
  );
};

// 布尔筛选组件
const BooleanFilter = ({ field, value, onChange }) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {field.label}
      </label>
      <div className="flex gap-2">
        {field.options.map(option => (
          <button
            key={option.value}
            onClick={() => onChange(field.id, option.value)}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${value === option.value
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const getDefaultValueForField = (field) => {
  switch (field.type) {
    case FILTER_TYPES.MULTI_SELECT:
      return [];
    case FILTER_TYPES.SINGLE_SELECT:
      return field.options?.[0]?.value ?? '';
    case FILTER_TYPES.DATE_RANGE:
      return { start: '', end: '', renewalPending: false };
    case FILTER_TYPES.NUMBER_RANGE:
      return { min: '', max: '' };
    case FILTER_TYPES.BOOLEAN:
      return field.options?.[0]?.value ?? null;
    default:
      return null;
  }
};

// 主筛选面板组件
export default function FilterPanel({ role, filters, onFiltersChange, talentCounts }) {
  const [config, setConfig] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      let filterConfig = getFilterConfigByRole(role);

      // 如果有动态字段（如销售员列表），加载数据
      const dynamicFields = filterConfig.filter(f => f.dynamic);
      if (dynamicFields.length > 0) {
        for (const field of dynamicFields) {
          if (field.id === 'salesStaffId') {
            try {
              const salesUsers = await apiService.getSalesUsers();
              field.options = [
                { value: '', label: 'すべて' },
                ...salesUsers.map(user => ({
                  value: user.id,
                  label: user.username,
                })),
              ];
            } catch (error) {
              console.error('Failed to load sales users:', error);
              field.options = [{ value: '', label: 'すべて' }];
            }
          }
        }
      }

      setConfig(filterConfig);
    };

    loadConfig();
  }, [role]);

  useEffect(() => {
    if (config.length === 0) return;
    const missingFields = config.filter(field => filters[field.id] === undefined);
    if (missingFields.length === 0) return;

    const initialFilters = getInitialFilters(config);
    const mergedFilters = { ...initialFilters, ...filters };
    onFiltersChange(mergedFilters);
  }, [config, filters, onFiltersChange]);

  const handleFilterChange = (fieldId, value) => {
    const newFilters = { ...filters, [fieldId]: value };

    if (fieldId === 'employmentDurationMaxDays' && value !== '') {
      newFilters.employmentStatus = '離職';
    }

    if (fieldId === 'employmentStatus' && value !== '離職' && filters.employmentDurationMaxDays) {
      newFilters.employmentDurationMaxDays = '';
    }

    onFiltersChange(newFilters);
  };

  useEffect(() => {
    if (filters?.employmentStatus === undefined) return;
    const statusValue = filters.employmentStatus;
    const shouldShow = statusValue === '在職' || statusValue === '離職';
    const shouldKeepEmploymentDuration = statusValue === '離職';
    const currentRange = filters.employmentStatusDate;
    const hasValue = currentRange && (currentRange.start || currentRange.end || currentRange.renewalPending);
    const hasEmploymentDurationFilter = Boolean(filters.employmentDurationMaxDays);

    if ((!shouldShow && hasValue) || (!shouldKeepEmploymentDuration && hasEmploymentDurationFilter)) {
      onFiltersChange({
        ...filters,
        ...(!shouldShow && hasValue ? { employmentStatusDate: { start: '', end: '', renewalPending: false } } : {}),
        ...(!shouldKeepEmploymentDuration && hasEmploymentDurationFilter ? { employmentDurationMaxDays: '' } : {})
      });
    }
  }, [filters, onFiltersChange]);

  const handleReset = () => {
    const initialFilters = getInitialFilters(config);
    onFiltersChange(initialFilters);
  };

  const activeCount = getActiveFilterCount(filters, config);

  const renderFilterField = (field) => {
    if (field.id === 'employmentStatusDate') {
      return null;
    }

    const value = filters[field.id] !== undefined
      ? filters[field.id]
      : getDefaultValueForField(field);

    switch (field.type) {
      case FILTER_TYPES.MULTI_SELECT:
        return (
          <MultiSelectFilter
            key={field.id}
            field={field}
            value={value}
            onChange={handleFilterChange}
            talentCounts={talentCounts}
          />
        );
      case FILTER_TYPES.SINGLE_SELECT:
        if (field.id === 'employmentStatus') {
          const statusLabel = EMPLOYMENT_STATUS_DATE_LABELS[value];
          const showDateRange = !!statusLabel;
          const statusDateValue = filters.employmentStatusDate !== undefined
            ? filters.employmentStatusDate
            : getDefaultValueForField({ type: FILTER_TYPES.DATE_RANGE });

          return (
            <div key={field.id}>
              <SingleSelectFilter
                field={field}
                value={value}
                onChange={handleFilterChange}
              />
              {showDateRange && (
                <div className="mb-4 pl-1">
                  <label className="block text-xs text-gray-600 mb-1">{statusLabel}期間:</label>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">開始日:</label>
                      <input
                        type="date"
                        value={statusDateValue.start}
                        onChange={(e) => handleFilterChange('employmentStatusDate', { ...statusDateValue, start: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">終了日:</label>
                      <input
                        type="date"
                        value={statusDateValue.end}
                        onChange={(e) => handleFilterChange('employmentStatusDate', { ...statusDateValue, end: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        }
        return (
          <SingleSelectFilter
            key={field.id}
            field={field}
            value={value}
            onChange={handleFilterChange}
          />
        );
      case FILTER_TYPES.DATE_RANGE:
        return (
          <DateRangeFilter
            key={field.id}
            field={field}
            value={value}
            onChange={handleFilterChange}
          />
        );
      case FILTER_TYPES.NUMBER_RANGE:
      {
        const noChargeValue = field.noChargeFilterId
          ? (filters[field.noChargeFilterId] ?? null)
          : null;
        const unregisteredValue = field.unregisteredFilterId
          ? (filters[field.unregisteredFilterId] ?? null)
          : null;
        const getDefaultRangeValue = () => ({ min: '', max: '' });
        const applyNumberRangeFlags = (nextNoChargeValue, nextUnregisteredValue) => {
          const shouldDisableRange = nextNoChargeValue === true || nextUnregisteredValue === true;
          onFiltersChange({
            ...filters,
            [field.id]: shouldDisableRange ? getDefaultRangeValue() : value,
            ...(field.noChargeFilterId ? { [field.noChargeFilterId]: nextNoChargeValue } : {}),
            ...(field.unregisteredFilterId ? { [field.unregisteredFilterId]: nextUnregisteredValue } : {}),
          });
        };
        const handleNoChargeToggle = (checked) => {
          const nextNoChargeValue = checked ? true : null;
          const nextUnregisteredValue = field.unregisteredFilterId
            ? (filters[field.unregisteredFilterId] ?? null)
            : null;
          applyNumberRangeFlags(nextNoChargeValue, nextUnregisteredValue);
        };
        const handleUnregisteredToggle = (checked) => {
          const nextNoChargeValue = field.noChargeFilterId
            ? (filters[field.noChargeFilterId] ?? null)
            : null;
          const nextUnregisteredValue = checked ? true : null;
          applyNumberRangeFlags(nextNoChargeValue, nextUnregisteredValue);
        };
        return (
          <NumberRangeFilter
            key={field.id}
            field={field}
            value={value}
            onChange={handleFilterChange}
            noChargeValue={noChargeValue}
            unregisteredValue={unregisteredValue}
            onNoChargeToggle={handleNoChargeToggle}
            onUnregisteredToggle={handleUnregisteredToggle}
          />
        );
      }
      case FILTER_TYPES.BOOLEAN:
        return (
          <BooleanFilter
            key={field.id}
            field={field}
            value={value}
            onChange={handleFilterChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ${isCollapsed ? 'w-12' : 'w-64'
      }`}>
      {/* 标题栏 */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        {!isCollapsed && (
          <div className="flex items-center">
            <FilterIcon />
            <h3 className="ml-2 text-lg font-semibold text-gray-800">フィルター</h3>
            {activeCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                {activeCount}
              </span>
            )}
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title={isCollapsed ? '展開' : '折りたたむ'}
        >
          <svg
            className={`w-5 h-5 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {!isCollapsed && (
        <>
          {/* 筛选字段列表 */}
          <div className="flex-1 overflow-y-auto p-4">
            {config.length > 0 ? (
              config.map(field => renderFilterField(field))
            ) : (
              <div className="text-center text-sm text-gray-500 py-8">
                フィルターを読み込み中...
              </div>
            )}
          </div>

          {/* 底部操作按钮 */}
          {activeCount > 0 && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleReset}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                リセット
              </button>
            </div>
          )}
        </>
      )}

      {/* 折叠状态下的筛选指示器 */}
      {isCollapsed && activeCount > 0 && (
        <div className="flex-1 flex flex-col items-center justify-start pt-4">
          <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
            {activeCount}
          </div>
        </div>
      )}
    </div>
  );
}
